import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import BugIcon from '@atlaskit/icon/core/bug';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import Tabs from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { QueryIntent } from '../../constants';

import { messages } from './messages';
import { DebuggerTabList } from './tab-list';
import { LoopPanel } from './tab-panels/loop-panel';
import { OrchestrationOverviewPanel } from './tab-panels/orchestration-overview-panel';
import { PluginInvocationsPanel } from './tab-panels/plugin-invocations-panel';
import { RankedSearchResultsPanel } from './tab-panels/ranked-search-results-panel';
import { RawJsonPanel } from './tab-panels/raw-json-panel';
import { RawSearchResultsPanel } from './tab-panels/raw-search-results-panel';
import { RequestOverviewPanel } from './tab-panels/request-overview-panel';
import { normalizeDebuggerData } from './utils';

const ColoredBugIcon = () => <BugIcon color={token('color.icon.subtlest')} label="" />;

export const OnlineDebuggerButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { commonAttributes, smartAnswersRoute } = useAIAnswerContext();
	const [isSAINDebuggerModalOpen, setIsSAINDebuggerModalOpen] = useState(false);

	const isNaturalLanguageQuery = smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY;

	const openDebuggerModal = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setIsSAINDebuggerModalOpen(true);
	}, []);
	const closeDebuggerModal = useCallback(() => setIsSAINDebuggerModalOpen(false), []);

	const { extraAttributes } = commonAttributes;
	const debuggerData = useMemo(() => normalizeDebuggerData(extraAttributes), [extraAttributes]);

	const showPluginsPanels = isNaturalLanguageQuery && !debuggerData.minions.hasMinionOutputs && debuggerData.pluginInvocations.hasPluginInvocations;

	return (
		<Fragment>
			<IconButton
				icon={ColoredBugIcon}
				label={formatMessage(messages.ai_debugger_icon_label)}
				onClick={openDebuggerModal}
				appearance="subtle"
				isTooltipDisabled={false}
				spacing="compact"
				testId="sain-online-debugger-button"
				color={token('color.icon.subtlest')}
				shape={fg('kd_sain_feedback_collector_ux_uplift_rollout') ? 'circle' : 'default'}
			/>

			<ModalTransition>
				{isSAINDebuggerModalOpen && (
					<Modal onClose={closeDebuggerModal} width="x-large" testId="sain-online-debugger-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Tabs id="sain-online-debugger-tabs">
								<DebuggerTabList
									isNaturalLanguageQuery={isNaturalLanguageQuery}
									hasMinionOutputs={debuggerData.minions.hasMinionOutputs}
									hasPluginInvocations={debuggerData.pluginInvocations.hasPluginInvocations}
									roundCount={debuggerData.minions.roundCount}
								/>
								<RequestOverviewPanel
									{...debuggerData.requestOverview}
									smartAnswersRoute={smartAnswersRoute}
									isNaturalLanguageQuery={isNaturalLanguageQuery}
								/>
								{/* Non-orchestration mode: show raw/ranked results and plugin invocations */}
								{showPluginsPanels && (
									<RawSearchResultsPanel searchResults={debuggerData.rawSearch.searchResults} />
								)}
								{showPluginsPanels && (
									<RankedSearchResultsPanel
										rankedDocumentsWithChunks={debuggerData.rankedSearch.rankedDocumentsWithChunks}
									/>
								)}
								{showPluginsPanels && (
									<PluginInvocationsPanel
										nonSearchPluginInvocations={
											debuggerData.pluginInvocations.nonSearchPluginInvocations
										}
									/>
								)}
								{/* Orchestration mode: show overview and loop panels */}
								{isNaturalLanguageQuery && (
									<OrchestrationOverviewPanel
										orchestrationRounds={debuggerData.minions.orchestrationRounds}
									/>
								)}
								{isNaturalLanguageQuery &&
									debuggerData.minions.hasMinionOutputs &&
									debuggerData.minions.orchestrationRounds.map((round, index) => (
										<LoopPanel key={`loop-panel-${index}`} round={round} roundIndex={index} />
									))}
								{isNaturalLanguageQuery && <RawJsonPanel rawJson={debuggerData.rawJson.rawJson} />}
							</Tabs>
						</ModalBody>
						<ModalFooter />
					</Modal>
				)}
			</ModalTransition>
		</Fragment>
	);
};
